import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAlert, useApi } from "../hooks";
import constants from "../utils/constants";
import { isInvalidID, switchSortDirection } from "../utils/helpers";
import { ActiveFilter, PrimaryTable } from "../components/Tables";
import dateHelpers from "../utils/dateHelpers";

const EMPTY_FILTERS = {
  activeOnly: false,
};

export default function Notifications({systemTenantId}) {
  const navigate = useNavigate();
  const alert = useAlert();
  const { fetch } = useApi();

  //Activity Variables
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    ...EMPTY_FILTERS,
    activeOnly: true,
  });
  const [stagedFilters, setStagedFilters] = useState(activeFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState(
    constants.SORT_DIRECTIONS.DESCENDING
  );
  const [sortField, setSortField] = useState("sentAt");
  const [displayCount, setDisplayCount] = useState(
    constants.DISPLAY_PER_PAGE ?? 25
  );
  const [notifications, setNotifications] = useState({ list: [], totalCount: 0 });

  useEffect(() => {
    if (isInvalidID(systemTenantId)) return;
    getNotificationList();
  }, [currentPage, displayCount, activeFilters, sortDirection, sortField]);

  const getNotificationList = () => {
    setIsLoading(true);
    fetch("admin/NotificationList", {
      pageNumber: currentPage,
      pageSize: displayCount,
      sortDirection: sortDirection,
      sortField: sortField,
      isExact: false,
      systemTenantId: systemTenantId,
      ...activeFilters,
    })
      .then((res) => {
        setNotifications(res);
      })
      .catch((err) => {
        alert("Server error", "Could not retrieve products", "error");
      })
      .finally(() => setIsLoading(false));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(switchSortDirection(sortDirection));
    } else {
      setSortField(field);
    }
  };

  if(!systemTenantId) return null;
  return (
    <div className="bg-gray-100 shadow-md rounded-md shadow-primaryColor/25">
      <PrimaryTable
        isLoading={isLoading}
        totalCount={notifications?.totalCount}
        displayCount={displayCount}
        currentPage={currentPage}
        setDisplayCount={setDisplayCount}
        setCurrentPage={setCurrentPage}
        header={
          <PrimaryTable.Header>
            <PrimaryTable.HeaderColumn
              display="Id"
              onSort={() => handleSort("id")}
              isSortingBy={sortField === "id"}
              sortDirection={sortDirection}
            />
            <PrimaryTable.HeaderColumn
              display="Tenant"
              onSort={() => handleSort("tenant")}
              isSortingBy={sortField === "tenant"}
              sortDirection={sortDirection}
            />
            <PrimaryTable.HeaderColumn
              display="Email"
              onSort={() => handleSort("email")}
              isSortingBy={sortField === "email"}
              sortDirection={sortDirection}
            />
            <PrimaryTable.HeaderColumn
              display="Phone"
              onSort={() => handleSort("phone")}
              isSortingBy={sortField === "phone"}
              sortDirection={sortDirection}
            />
            <PrimaryTable.HeaderColumn
              display="Method"
              onSort={() => handleSort("notificationMethod")}
              isSortingBy={sortField === "notificationMethod"}
              sortDirection={sortDirection}
            />
            <PrimaryTable.HeaderColumn
              display="Sent At"
              onSort={() => handleSort("sentAt")}
              isSortingBy={sortField === "sentAt"}
              sortDirection={sortDirection}
            />
          </PrimaryTable.Header>
        }
      >
        {notifications &&
          notifications.list &&
          notifications.list.map((x, i) => (
            <PrimaryTable.Row
              key={`${x.id}-${i}`}
              index={i}
              onClick={(e) => {
                if (!x.deactivatedAt) {
                  navigate(`/notification/${systemTenantId}/${x.id}`);
                }
              }}
            >
              <PrimaryTable.Cell>
                {x.id}
              </PrimaryTable.Cell>
              <PrimaryTable.Cell>
                {x.tenantName}
              </PrimaryTable.Cell>
              <PrimaryTable.Cell>
                {x.email}
              </PrimaryTable.Cell>
              <PrimaryTable.Cell>
                {x.mobilePhone}
              </PrimaryTable.Cell>
              <PrimaryTable.Cell>
                {x.notificationMethod}
              </PrimaryTable.Cell>
              <PrimaryTable.Cell>
                {dateHelpers.localDateTime(x.sentAt, dateHelpers.YMDHMSA)}
              </PrimaryTable.Cell>
        </PrimaryTable.Row>
      ))}
      </PrimaryTable>
    </div>
  );
}
